import React, { useEffect, useState, useCallback } from "react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import api from "@/lib/api";
import { fmtEUR, MONTHS_FR } from "@/lib/format";
import { CARD, Label, Loader, ErrorRetry } from "@/components/common";
import { SiteAnalysis } from "@/components/SiteRevenue";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const now = new Date();
const PALETTE = ["#065F46", "#0EA5E9", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316", "#6366F1"];

export default function Analyses() {
  const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try { const { data } = await api.get(`/analyses?year=${ym.year}&month=${ym.month}`); setDash(data); }
    catch (e) { setError(true); }
    finally { setLoading(false); }
  }, [ym]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Loader />;
  if (error || !dash) return <ErrorRetry onRetry={load} />;

  const barData = dash.twelve_months.map((m) => ({ name: m.short, Entrées: m.entrees, Dépenses: m.depenses, realise: m.realise }));
  const realIncome = dash.income_source === "realise";

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-gray-400">Comprendre vos chiffres</p>
          <h1 className="font-display text-2xl font-bold sm:text-3xl">Analyses</h1>
        </div>
        <Select value={`${ym.year}-${ym.month}`} onValueChange={(v) => { const [y, m] = v.split("-"); setYm({ year: +y, month: +m }); }}>
          <SelectTrigger className="w-44 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Array.from({ length: 15 }).map((_, i) => {
              const d = new Date(now.getFullYear(), now.getMonth() - 3 + i, 1);
              return <SelectItem key={i} value={`${d.getFullYear()}-${d.getMonth() + 1}`}>{MONTHS_FR[d.getMonth() + 1]} {d.getFullYear()}</SelectItem>;
            })}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <div className={`${CARD} p-6`}>
          <Label className="mb-3">Répartition des dépenses</Label>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={dash.expense_breakdown} dataKey="amount" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {dash.expense_breakdown.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtEUR(v)} />
                <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={`${CARD} p-6`}>
          <Label className="mb-1">Provenance des recettes</Label>
          <p className="mb-2 text-xs text-gray-400" data-testid="income-source">{realIncome ? "Encaissements réellement saisis, par mode de paiement" : "Prévisions (mois à venir)"}</p>
          {dash.income_breakdown.length === 0 ? <p className="py-16 text-center text-sm text-gray-400">Aucun encaissement saisi ce mois-ci.</p> : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={dash.income_breakdown} dataKey="amount" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {dash.income_breakdown.map((_, i) => <Cell key={i} fill={PALETTE[(i + 2) % PALETTE.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtEUR(v)} />
                <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          )}
        </div>

        <div className={`${CARD} p-6 lg:col-span-2`}>
          <Label className="mb-1">Entrées vs dépenses — 12 mois</Label>
          <p className="mb-2 text-xs text-gray-400">Entrées : encaissements saisis pour les mois écoulés et le mois en cours (vert foncé), prévisions ensuite (vert clair).</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F2F4" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip formatter={(v) => fmtEUR(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Entrées" fill="#065F46" radius={[6, 6, 0, 0]}>
                  {barData.map((b, i) => <Cell key={i} fill={b.realise ? "#065F46" : "#6EE7B7"} />)}
                </Bar>
                <Bar dataKey="Dépenses" fill="#F59E0B" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={`${CARD} p-6 lg:col-span-2`}>
          <Label className="mb-3">Top 5 dépenses du mois</Label>
          <div className="space-y-2.5">
            {dash.top_expenses.map((e, i) => {
              const max = dash.top_expenses[0]?.amount || 1;
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className="w-6 text-sm font-bold text-gray-300">{i + 1}</span>
                  <div className="flex-1">
                    <div className="flex justify-between text-sm"><span className="font-medium">{e.name}</span><span className="font-semibold">{fmtEUR(e.amount)}</span></div>
                    <div className="mt-1 h-2 rounded-full bg-gray-100"><div className="h-2 rounded-full" style={{ width: `${(e.amount / max) * 100}%`, background: PALETTE[i % PALETTE.length] }} /></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <SiteAnalysis year={ym.year} month={ym.month} />
      </div>
    </div>
  );
}
